import React, { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { GenericFormPage } from '../../components/generic';
import { useUser, User } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';
import UserPhotoSection from './components/UserPhotoSection';

const Form: React.FC = () => {
  const location = useLocation();
  const { id: idParam } = useParams<{ id: string }>();
  const isEditMode = location.pathname.includes('/edit');
  const editUserId = idParam && idParam !== 'new' ? parseInt(idParam, 10) : NaN;
  const { mutate: mutateUser } = useUser(isEditMode && Number.isInteger(editUserId) ? editUserId : 0);

  const configWithPasswordHandling = useMemo(() => {
    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'userPassword') {
        return {
          ...field,
          required: !isEditMode,
          placeholder: isEditMode ? 'Leave blank to keep current password' : 'Enter password',
          validation: isEditMode ? {
            ...field.validation,
            required: false,
          } : field.validation,
        };
      }
      return field;
    });

    const customCreate = async (data: Partial<User & { userPassword?: string }>) => {
      return await moduleConfig.form.create(data);
    };

    const customUpdate = async (id: number, data: Partial<User & { userPassword?: string }>) => {
      const updateData = { ...data };
      if (updateData.userPassword === '' || updateData.userPassword === undefined || updateData.userPassword === null) {
        delete updateData.userPassword;
      }
      return await moduleConfig.form.update(id, updateData);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData,
      mapItemToFormData,
      form: {
        ...moduleConfig.form,
        create: customCreate,
        update: customUpdate,
      },
      renderEditFormHeader: ({ isEdit, data, id }: { isEdit: boolean; data?: User; id?: number }) => {
        if (!isEdit || !id || !data) return null;
        return (
          <UserPhotoSection
            userId={id}
            user={data}
            onPhotoChange={() => {
              void mutateUser();
            }}
          />
        );
      },
    };
  }, [isEditMode, mutateUser]);

  return (
    <GenericFormPage<User & { userPassword?: string }>
      useData={useUser}
      config={configWithPasswordHandling}
    />
  );
};

export default Form;
